import { useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from '../components/core';
import { getOrgBySlug, getOrgMember, type Org } from '../services/org.service';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionsContext';
import { OrgContext } from './org-context';

interface OrgProviderProps {
  children: ReactNode;
}

type OrgState =
  | { status: 'loading' }
  | { status: 'not_found' }
  | { status: 'not_member' }
  | { status: 'disabled' }
  | { status: 'ready'; org: Org; isReadOnly: boolean };

export function OrgProvider({ children }: OrgProviderProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { user } = useAuth();
  const { appUser, isLoadingPermissions } = usePermissions();
  const [state, setState] = useState<OrgState>({ status: 'loading' });

  useEffect(() => {
    // Wait for auth + appUser to resolve before doing anything.
    if (!user || isLoadingPermissions || !appUser) return;
    if (!orgSlug) {
      setState({ status: 'not_found' });
      return;
    }

    setState({ status: 'loading' });
    let cancelled = false;

    async function resolve() {
      const org = await getOrgBySlug(orgSlug!);
      if (cancelled) return;

      if (!org) {
        setState({ status: 'not_found' });
        return;
      }

      // Check if org is disabled (non-super-admins are blocked)
      if (org.isDisabled && !appUser!.isSuperAdmin) {
        setState({ status: 'disabled' });
        return;
      }

      // Super admins bypass the membership check and get read-only access.
      if (appUser!.isSuperAdmin) {
        setState({ status: 'ready', org, isReadOnly: true });
        return;
      }

      const member = await getOrgMember(org.id, user!.uid);
      if (cancelled) return;

      if (!member || member.isDisabled) {
        setState({ status: 'not_member' });
        return;
      }

      setState({ status: 'ready', org, isReadOnly: false });
    }

    resolve().catch(() => {
      if (!cancelled) setState({ status: 'not_found' });
    });

    return () => { cancelled = true; };
  }, [orgSlug, user, appUser, isLoadingPermissions]);

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (state.status === 'not_found') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
        <p className="text-lg font-semibold text-gray-900">Organisation not found</p>
        <p className="text-sm text-gray-500">The organisation you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/" className="mt-2 text-sm text-green-700 hover:underline">Go home</Link>
      </div>
    );
  }

  if (state.status === 'not_member') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
        <p className="text-lg font-semibold text-gray-900">Access denied</p>
        <p className="text-sm text-gray-500">You are not a member of this organisation.</p>
        <Link to="/" className="mt-2 text-sm text-green-700 hover:underline">Go home</Link>
      </div>
    );
  }

  if (state.status === 'disabled') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
        <p className="text-lg font-semibold text-gray-900">Your organisation has been disabled</p>
        <p className="text-sm text-gray-500">Please contact support for more information.</p>
        <Link to="/" className="mt-2 text-sm text-green-700 hover:underline">Go home</Link>
      </div>
    );
  }

  return (
    <OrgContext.Provider
      value={{
        org: state.org,
        orgId: state.org.id,
        orgSlug: state.org.slug,
        isReadOnly: state.isReadOnly,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}
